import React, { useState, useEffect, useCallback, useContext, useRef } from "react";
import "./AgendaInviteDrawer.css";
import { LuX, LuSearch, LuLoader2 } from "react-icons/lu";
import { IoRadioButtonOn, IoRadioButtonOff, IoCheckmarkCircle } from "react-icons/io5";
import axios from "axios";
import { Language } from "../../App";
import { adminBaseURL, _imagesURL } from "../../constants/API";
import { authorize } from "../../shared/authorize";
import { useCustomToast } from "../common/CustomToast";
import { releaseAgendaScrollLock } from "../../utils/agendaUtils";
import AvatarPlaceholder from "../../assets/images/icon/placeholder_Awatar.png";

const EXIT_ANIMATION_DURATION = 220;

export default function AgendaInviteDrawer({
  isOpen,
  toggle,
  itemId,
  type = "webinar", // "webinar", "clinical_session", or "event"
  onDone,
}) {
  const { lang } = useContext(Language);
  const { showToast } = useCustomToast();

  const SplashData = JSON.parse(localStorage.getItem("SplashData") || "{}");
  const currentUserId = SplashData?.user?.id;

  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const [userList, setUserList] = useState([]);
  const [paginationData, setPaginationData] = useState({});
  const [currentPage, setCurrentPage] = useState(1);

  const [searchList, setSearchList] = useState([]);
  const [searchPagination, setSearchPagination] = useState({});
  const [searchPage, setSearchPage] = useState(1);

  // Set of selected user IDs (checked in UI)
  const [selectedUserIds, setSelectedUserIds] = useState(new Set());
  // Set of user IDs that were already marked as invited on the server
  const [alreadyInvitedIds, setAlreadyInvitedIds] = useState(new Set());
  // Set of user IDs that have reserved their spot / attendees
  const [reservedUserIds, setReservedUserIds] = useState(new Set());
  const [shouldRender, setShouldRender] = useState(isOpen);

  const listContainerRef = useRef(null);

  const isWebinar = type === "webinar";
  const isClinicalSession = type === "clinical_session";
  const isEvent = type === "event";

  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      document.body.style.overflow = "hidden";
      return undefined;
    }

    releaseAgendaScrollLock(true);

    if (!shouldRender) return undefined;

    const closeTimer = window.setTimeout(() => {
      setShouldRender(false);
      releaseAgendaScrollLock(true);
    }, EXIT_ANIMATION_DURATION);

    return () => {
      window.clearTimeout(closeTimer);
      releaseAgendaScrollLock(true);
    };
  }, [isOpen, shouldRender]);


  // Helper to normalize user objects across different endpoints
  const normalizeUser = useCallback((u) => {
    if (!u || (!u.id && u.id !== 0)) return null;
    return {
      ...u,
      id: u.id,
      name:
        u.name ||
        `${u.first_name || ""} ${u.last_name || ""}`.trim() ||
        u.user_name ||
        u.username ||
        "Doctor",
      user_name: u.user_name || u.username || "",
      specialization:
        u.specialization ||
        u.category ||
        u.specialty ||
        u.subcategory ||
        "",
      image: u.image || u.thumbnail || u.avatar || u.profile_image || "",
    };
  }, []);

  // Fetch initial data: merges ALL reserved attendees, ALL invited users, and suggested users
  const loadInitialData = useCallback(async () => {
    if (!itemId) return;
    setIsLoading(true);

    try {
      let reservedEndpoint = `${adminBaseURL}user/post/webinar/reserved/users/${itemId}`;
      let invitedEndpoint = `${adminBaseURL}user/post/webinar/invited/users/${itemId}`;
      let usersEndpoint = `${adminBaseURL}user/post/webinar/users/${itemId}?page=1`;

      if (isClinicalSession) {
        reservedEndpoint = `${adminBaseURL}user/post/clinical-session/reserved/users/${itemId}`;
        invitedEndpoint = `${adminBaseURL}user/post/clinical-session/invited/users/${itemId}`;
        usersEndpoint = `${adminBaseURL}user/post/clinical-session/users/${itemId}?page=1`;
      } else if (isEvent) {
        reservedEndpoint = `${adminBaseURL}user/event/reserved/users/${itemId}`;
        invitedEndpoint = `${adminBaseURL}user/event/invited/users/${itemId}`;
        usersEndpoint = `${adminBaseURL}user/event/users/${itemId}?page=1`;
      }

      // Fetch in parallel
      const [resReserved, resInvited, resUsers] = await Promise.allSettled([
        axios.get(reservedEndpoint, authorize()).catch((err) => {
          if (isEvent) {
            return axios.get(
              `${adminBaseURL}user/post/webinar/reserved/users/${itemId}`,
              authorize()
            );
          }
          throw err;
        }),
        axios.get(invitedEndpoint, authorize()).catch((err) => {
          if (isEvent) {
            return axios.get(
              `${adminBaseURL}user/post/webinar/invited/users/${itemId}`,
              authorize()
            );
          }
          throw err;
        }),
        axios.get(usersEndpoint, authorize()).catch((err) => {
          if (isEvent) {
            return axios.get(
              `${adminBaseURL}user/post/webinar/users/${itemId}?page=1`,
              authorize()
            );
          }
          throw err;
        }),
      ]);

      const reservedMap = new Map();
      const invitedMap = new Map();
      const combinedMap = new Map();

      // 1. Process Reserved Users (Attendees)
      if (resReserved.status === "fulfilled" && resReserved.value?.data?.status) {
        const rawReserved = Array.isArray(resReserved.value.data.data?.data)
          ? resReserved.value.data.data.data
          : Array.isArray(resReserved.value.data.data)
          ? resReserved.value.data.data
          : [];

        rawReserved.forEach((u) => {
          const norm = normalizeUser(u);
          if (norm) {
            norm.is_reserved = 1;
            reservedMap.set(norm.id, norm);
            combinedMap.set(norm.id, norm);
          }
        });
      }

      // 2. Process Already Invited Users
      if (resInvited.status === "fulfilled" && resInvited.value?.data?.status) {
        const rawInvited = Array.isArray(resInvited.value.data.data?.data)
          ? resInvited.value.data.data.data
          : Array.isArray(resInvited.value.data.data)
          ? resInvited.value.data.data
          : [];

        rawInvited.forEach((u) => {
          const norm = normalizeUser(u);
          if (norm) {
            norm.is_invited = 1;
            invitedMap.set(norm.id, norm);
            if (!combinedMap.has(norm.id)) {
              combinedMap.set(norm.id, norm);
            }
          }
        });
      }

      // 3. Process Suggested / Followed Users (Page 1)
      if (resUsers.status === "fulfilled" && resUsers.value?.data?.status) {
        const usersPayload = resUsers.value.data.data;
        const rawUsers = Array.isArray(usersPayload?.data)
          ? usersPayload.data
          : Array.isArray(usersPayload)
          ? usersPayload
          : [];

        rawUsers.forEach((u) => {
          const norm = normalizeUser(u);
          if (norm) {
            const isRes = Boolean(
              norm.reserve ||
                norm.is_reserve ||
                norm.is_reserved ||
                norm.reserved ||
                norm.is_joined ||
                norm.isJoined
            );
            const isInv = Boolean(
              norm.invite || norm.is_invited || norm.isInvited
            );

            if (isRes) {
              norm.is_reserved = 1;
              reservedMap.set(norm.id, norm);
            } else if (isInv) {
              norm.is_invited = 1;
              invitedMap.set(norm.id, norm);
            }

            if (!combinedMap.has(norm.id)) {
              combinedMap.set(norm.id, norm);
            }
          }
        });

        setPaginationData(usersPayload || {});
        setCurrentPage(usersPayload?.current_page || 1);
      }

      const reservedIdSet = new Set(reservedMap.keys());
      const invitedIdSet = new Set(invitedMap.keys());

      setReservedUserIds(reservedIdSet);
      setAlreadyInvitedIds(invitedIdSet);
      setSelectedUserIds(new Set([...invitedIdSet]));
      setUserList(Array.from(combinedMap.values()));
    } catch (err) {
      console.error("Error loading initial invite drawer data:", err);
    } finally {
      setIsLoading(false);
    }
  }, [itemId, isClinicalSession, isEvent, normalizeUser]);

  // Fetch paginated users list (page > 1)
  const fetchUsers = useCallback(
    async (page = 2) => {
      if (!itemId) return;
      setIsLoadingMore(true);

      try {
        let endpoint = `${adminBaseURL}user/post/webinar/users/${itemId}?page=${page}`;
        if (isClinicalSession) {
          endpoint = `${adminBaseURL}user/post/clinical-session/users/${itemId}?page=${page}`;
        } else if (isEvent) {
          endpoint = `${adminBaseURL}user/event/users/${itemId}?page=${page}`;
        }

        let res;
        try {
          res = await axios.get(endpoint, authorize());
        } catch (fetchErr) {
          if (isEvent) {
            res = await axios.get(
              `${adminBaseURL}user/post/webinar/users/${itemId}?page=${page}`,
              authorize()
            );
          } else {
            throw fetchErr;
          }
        }

        if (res?.data?.status) {
          const list = res.data.data?.data || [];
          const normalizedNewUsers = [];

          list.forEach((u) => {
            const norm = normalizeUser(u);
            if (norm) {
              const isRes = Boolean(
                norm.reserve ||
                  norm.is_reserve ||
                  norm.is_reserved ||
                  norm.reserved ||
                  norm.is_joined ||
                  norm.isJoined
              );
              const isInv = Boolean(
                norm.invite || norm.is_invited || norm.isInvited
              );

              if (isRes) {
                setReservedUserIds((prev) => new Set([...prev, norm.id]));
              }
              if (isInv) {
                setAlreadyInvitedIds((prev) => new Set([...prev, norm.id]));
                setSelectedUserIds((prev) => new Set([...prev, norm.id]));
              }

              normalizedNewUsers.push(norm);
            }
          });

          setUserList((prev) => {
            const existingIds = new Set(prev.map((item) => item.id));
            const fresh = normalizedNewUsers.filter((item) => !existingIds.has(item.id));
            return [...prev, ...fresh];
          });

          setPaginationData(res.data.data || {});
          setCurrentPage(res.data.data?.current_page || page);
        }
      } catch (err) {
        console.error("Error fetching more invitation users:", err);
      } finally {
        setIsLoadingMore(false);
      }
    },
    [itemId, isClinicalSession, isEvent, normalizeUser]
  );

  // Stable debounced search API — useRef ensures the debounced fn is never recreated
  const searchApiRef = useRef(null);
  if (!searchApiRef.current) {
    searchApiRef.current = (function buildDebouncedSearch() {
      let timer = null;
      return function debouncedSearch(keyword, page, opts) {
        if (timer) clearTimeout(timer);
        timer = setTimeout(() => {
          opts.run(keyword, page);
        }, 400);
      };
    })();
  }

  // The actual async search executor (stable ref so debounce captures latest values)
  const searchExecutorRef = useRef(null);
  searchExecutorRef.current = useCallback(
    async (keyword, page = 1) => {
      if (!keyword.trim()) {
        setSearchList([]);
        setSearchPagination({});
        setIsSearching(false);
        return;
      }

      if (page === 1) {
        setIsSearching(true);
      } else {
        setIsLoadingMore(true);
      }

      try {
        let endpoint = `${adminBaseURL}user/post/webinar/user/search`;
        if (isClinicalSession) {
          endpoint = `${adminBaseURL}user/post/clinical-session/user/search`;
        } else if (isEvent) {
          endpoint = `${adminBaseURL}user/event/user/search`;
        }

        const payload = isClinicalSession
          ? { keyword, user_id: currentUserId, post_id: itemId, page }
          : { keyword, post_id: itemId, page };

        let res;
        try {
          res = await axios.post(endpoint, payload, authorize());
        } catch (searchErr) {
          if (isEvent) {
            res = await axios.post(
              `${adminBaseURL}user/post/webinar/user/search`,
              payload,
              authorize()
            );
          } else {
            throw searchErr;
          }
        }

        if (res?.data?.status) {
          const list = res.data.data?.data || [];
          const normalizedSearch = list.map(normalizeUser).filter(Boolean);

          if (page === 1) {
            setSearchList(normalizedSearch);
          } else {
            setSearchList((prev) => [...prev, ...normalizedSearch]);
          }

          const serverInvited = list
            .filter((u) => Boolean(u.invite || u.is_invited || u.isInvited))
            .map((u) => u.id);
          if (serverInvited.length > 0) {
            setAlreadyInvitedIds((prev) => new Set([...prev, ...serverInvited]));
            setSelectedUserIds((prev) => new Set([...prev, ...serverInvited]));
          }

          const serverReserved = list
            .filter((u) =>
              Boolean(
                u.reserve ||
                  u.is_reserve ||
                  u.is_reserved ||
                  u.reserved ||
                  u.is_joined ||
                  u.isJoined
              )
            )
            .map((u) => u.id);
          if (serverReserved.length > 0) {
            setReservedUserIds((prev) => new Set([...prev, ...serverReserved]));
          }

          setSearchPagination(res.data.data || {});
          setSearchPage(res.data.data?.current_page || page);
        } else {
          if (page === 1) {
            setSearchList([]);
          }
        }
      } catch (err) {
        console.error("Error searching users:", err);
      } finally {
        setIsSearching(false);
        setIsLoadingMore(false);
      }
    },
    [itemId, isClinicalSession, isEvent, currentUserId, normalizeUser]
  );

  // Handle Search Input Change
  const onSearchChange = (e) => {
    const val = e.target.value;
    setSearchTerm(val);
    setSearchPage(1);
    if (val.trim()) {
      searchApiRef.current(val, 1, { run: (kw, pg) => searchExecutorRef.current(kw, pg) });
    } else {
      setSearchList([]);
      setIsSearching(false);
    }
  };

  // Reset & load initial users when drawer opens
  useEffect(() => {
    if (isOpen && itemId) {
      setSearchTerm("");
      setCurrentPage(1);
      setUserList([]);
      setSearchList([]);
      setSelectedUserIds(new Set());
      setAlreadyInvitedIds(new Set());
      setReservedUserIds(new Set());
      loadInitialData();
    }
  }, [isOpen, itemId, loadInitialData]);

  // Check if user is already reserved (attendee)
  const isUserReserved = useCallback(
    (u) =>
      reservedUserIds.has(u.id) ||
      Boolean(
        u.reserve ||
          u.is_reserve ||
          u.is_reserved ||
          u.reserved ||
          u.is_joined ||
          u.isJoined ||
          u.joined ||
          u.status === "reserved" ||
          u.status === "joined"
      ),
    [reservedUserIds]
  );

  // Check if user is already invited
  const isUserInvited = useCallback(
    (u) =>
      alreadyInvitedIds.has(u.id) ||
      Boolean(u.invite || u.is_invited || u.isInvited),
    [alreadyInvitedIds]
  );

  // Local Selection Toggle
  const handleToggleSelection = (userId, userObj) => {
    if (userObj && (isUserReserved(userObj) || isUserInvited(userObj))) return;
    setSelectedUserIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
  };

  const currentList = searchTerm.trim() ? searchList : userList;
  const selectableUsers = currentList.filter(
    (u) => !isUserReserved(u) && !isUserInvited(u)
  );

  // Select all or deselect all (only targets selectable users)
  const handleSelectAll = () => {
    if (selectableUsers.length === 0) return;
    const allSelectableIds = selectableUsers.map((u) => u.id);
    const allSelected = allSelectableIds.every((id) => selectedUserIds.has(id));

    setSelectedUserIds((prev) => {
      const next = new Set(prev);
      if (allSelected) {
        allSelectableIds.forEach((id) => next.delete(id));
      } else {
        allSelectableIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  // Infinite scroll on list container
  const handleScroll = () => {
    if (!listContainerRef.current || isLoading || isLoadingMore) return;
    const { scrollTop, scrollHeight, clientHeight } = listContainerRef.current;
    if (scrollTop + clientHeight >= scrollHeight - 40) {
      if (searchTerm.trim()) {
        if (searchPagination?.current_page < searchPagination?.last_page) {
          searchApiRef.current(searchTerm, searchPagination.current_page + 1, {
            run: (kw, pg) => searchExecutorRef.current(kw, pg),
          });
        }
      } else {
        if (paginationData?.current_page < paginationData?.last_page) {
          fetchUsers(paginationData.current_page + 1);
        }
      }
    }
  };

  // Bulk invite: single API call with comma-separated user IDs
  const handleSendInvites = async () => {
    const usersToInvite = Array.from(selectedUserIds).filter(
      (id) => !alreadyInvitedIds.has(id) && !reservedUserIds.has(id)
    );

    if (usersToInvite.length === 0) {
      if (onDone) onDone();
      toggle();
      return;
    }

    setIsSending(true);

    try {
      const userIdsParam = usersToInvite.join(",");

      let endpoint = `${adminBaseURL}user/post/webinar/invite/multiple/${itemId}/${userIdsParam}`;
      if (isClinicalSession) {
        endpoint = `${adminBaseURL}user/post/clinical-session/invite/multiple/${itemId}/${userIdsParam}`;
      } else if (isEvent) {
        endpoint = `${adminBaseURL}user/event/invite/multiple/${itemId}/${userIdsParam}`;
      }

      let res;
      try {
        res = await axios.get(endpoint, authorize());
      } catch (sendErr) {
        if (isEvent) {
          res = await axios.get(
            `${adminBaseURL}user/post/webinar/invite/multiple/${itemId}/${userIdsParam}`,
            authorize()
          );
        } else {
          throw sendErr;
        }
      }

      if (res?.data?.status) {
        const count = usersToInvite.length;
        const title =
          count > 1
            ? (lang?.invitations_sent_title || "{count} Invitations Sent").replace("{count}", count)
            : (lang?.invitation_sent_title || "1 Invitation Sent");
        const message =
          count > 1
            ? (lang?.invitations_sent_desc || "{count} invitations have been sent successfully.").replace("{count}", count)
            : (lang?.invitation_sent_desc || "Invitation has been sent successfully.");

        showToast({
          title,
          message,
          type: "success",
        });

        setAlreadyInvitedIds((prev) => new Set([...prev, ...usersToInvite]));

        if (onDone) onDone();
        toggle();
      } else {
        showToast(
          res?.data?.error ||
            res?.data?.message ||
            (lang?.failed_to_send_invitations || "Failed to send invitations. Please try again."),
          "error"
        );
      }
    } catch (err) {
      console.error("Error sending bulk invitations:", err);
      showToast(
        err?.response?.data?.error || err?.message || (lang?.failed_to_send_invitations || "Failed to send invitations"),
        "error"
      );
    } finally {
      setIsSending(false);
    }
  };

  if (!shouldRender) return null;

  const newlySelectedCount = Array.from(selectedUserIds).filter(
    (id) => !alreadyInvitedIds.has(id) && !reservedUserIds.has(id)
  ).length;

  return (
    <>
      {/* Backdrop */}
      <div
        className={`agenda-invite-backdrop ${isOpen ? "open" : "closing"}`}
        onClick={toggle}
      />

      {/* Slide-in Drawer Container */}
      <div className={`agenda-invite-drawer ${isOpen ? "open" : "closing"}`}>
        {/* Header Bar (Title + Close Button) */}
        <div className="agenda-invite-header-row">
          <div className="d-flex flex-column gap-1">
            <h3 className="agenda-invite-title">
              {lang?.send_invites || "Send Invites"}
            </h3>
            <p className="agenda-invite-subtitle">
              {isEvent
                ? (lang?.invite_users_event || "Invite users to attend your event")
                : isWebinar
                ? (lang?.invite_users_webinar || "Invite users to attend your webinar")
                : (lang?.invite_users_session || "Invite users to attend your clinical session")}
            </p>
          </div>

       
        </div>

        {/* Search Bar (Figma Frame 2147232750) */}
        <div className="agenda-invite-search-wrap">
          <LuSearch size={18} color="#8F98A3" className="agenda-invite-search-icon" />
          <input
            type="text"
            className="agenda-invite-search-input"
            placeholder={lang?.search || "Search"}
            value={searchTerm}
            onChange={onSearchChange}
            disabled={isSending}
          />
          {searchTerm && (
            <button
              type="button"
              className="agenda-invite-search-clear"
              onClick={() => {
                setSearchTerm("");
                setSearchList([]);
              }}
            >
              <LuX size={14} />
            </button>
          )}
        </div>

        {/* List Toolbar (Counts + Select All) */}
        {currentList.length > 0 && (
          <div className="agenda-invite-list-toolbar">
            <span className="agenda-invite-list-count">
              {searchTerm
                ? `${lang?.results || "Results"} (${currentList.length})`
                : `${lang?.suggested || "Suggested"} (${currentList.length})`}
            </span>
            {selectableUsers.length > 0 && (
              <button
                type="button"
                className="agenda-invite-select-all-btn"
                onClick={handleSelectAll}
                disabled={isSending}
              >
                {selectableUsers.every((u) => selectedUserIds.has(u.id))
                  ? (lang?.deselect_all || "Deselect All")
                  : (lang?.select_all || "Select All")}
              </button>
            )}
          </div>
        )}

        {/* User List Area (Figma Frame 1686558046) */}
        <div
          className="agenda-invite-list-container"
          ref={listContainerRef}
          onScroll={handleScroll}
        >
          {isLoading || isSearching ? (
            <div className="agenda-invite-loading-box">
              <LuLoader2 size={28} color="#00BADA" className="agenda-spin-anim" />
              <span>{lang?.loading_users || "Loading users..."}</span>
            </div>
          ) : currentList.length === 0 ? (
            <div className="agenda-invite-empty-box">
              <p className="agenda-invite-empty-title">
                {searchTerm
                  ? (lang?.no_users_found_search || "No users found matching your search")
                  : (lang?.no_users_available_invite || "No users available for invite")}
              </p>
            </div>
          ) : (
            currentList.map((user, idx) => {
              const avatarSrc = user.image ? `${_imagesURL}${user.image}` : AvatarPlaceholder;
              const isReserved = isUserReserved(user);
              const isAlreadyInvited = !isReserved && isUserInvited(user);
              const isSelected = isReserved || isAlreadyInvited || selectedUserIds.has(user.id);

              return (
                <div key={user.id || idx} className="agenda-invite-item-wrapper">
                  <div
                    className={`agenda-invite-item-row ${
                      isReserved
                        ? "is-reserved"
                        : isAlreadyInvited
                        ? "is-invited"
                        : isSelected
                        ? "invited"
                        : ""
                    }`}
                    onClick={() =>
                      !isSending &&
                      !isReserved &&
                      !isAlreadyInvited &&
                      handleToggleSelection(user.id, user)
                    }
                  >
                    {/* Left: Avatar + Details */}
                    <div className="agenda-invite-user-info">
                      <img
                        src={avatarSrc}
                        alt={user.name || "User Avatar"}
                        className="agenda-invite-avatar"
                        onError={(e) => {
                          e.target.src = AvatarPlaceholder;
                        }}
                      />
                      <div className="agenda-invite-text-col">
                        <div className="d-flex align-items-center gap-2">
                          <span className="agenda-invite-name" title={user.name}>
                            {user.name || (lang?.doctor || "Doctor")}
                          </span>
                          {isReserved ? (
                            <span className="agenda-invite-badge-reserved">
                              {lang?.reserved || "Reserved"}
                            </span>
                          ) : isAlreadyInvited ? (
                            <span className="agenda-invite-badge-invited">
                              {lang?.invited || "Invited"}
                            </span>
                          ) : null}
                        </div>
                        <span className="agenda-invite-role">
                          {user.specialization || user.user_name || (lang?.specialist || "Specialist")}
                        </span>
                      </div>
                    </div>

                    {/* Right: Radio Button Selector or Reserved Checkmark */}
                    <div className="agenda-invite-selector-wrap">
                      {isReserved ? (
                        <IoCheckmarkCircle size={20} color="#10B981" title={lang?.reserved || "Reserved"} />
                      ) : isAlreadyInvited ? (
                        <IoRadioButtonOn size={20} color="#00BADA" title={lang?.invited || "Invited"} />
                      ) : isSelected ? (
                        <IoRadioButtonOn size={20} color="#00BADA" />
                      ) : (
                        <IoRadioButtonOff size={20} className="agenda-invite-radio-off" />
                      )}
                    </div>
                  </div>

                  {/* Divider (Figma Rectangle 28 / 29 / 30) */}
                  <div className="agenda-invite-divider" />
                </div>
              );
            })
          )}

          {isLoadingMore && (
            <div className="agenda-invite-loading-more">
              <LuLoader2 size={18} color="#00BADA" className="agenda-spin-anim" />
              <span>{lang?.loading_more || "Loading more..."}</span>
            </div>
          )}
        </div>

        {/* Sticky Footer (Figma Frame 1686558034) */}
        <div className="agenda-invite-footer">
          <button
            type="button"
            className="agenda-invite-submit-btn"
            onClick={handleSendInvites}
            disabled={isSending}
          >
            {isSending ? (
              <span style={{ display: "inline-flex", alignItems: "center", gap: "8px" }}>
                <LuLoader2 size={18} color="#FFFFFF" className="agenda-spin-anim" />
                <span>{lang?.sending || "Sending"} {newlySelectedCount > 0 ? `(${newlySelectedCount})` : ""}...</span>
              </span>
            ) : newlySelectedCount > 0 ? (
              `${lang?.send_invites || "Send Invites"} (${newlySelectedCount})`
            ) : selectedUserIds.size > 0 ? (
              lang?.done || "Done"
            ) : (
              lang?.send_invites || "Send Invites"
            )}
          </button>
        </div>
      </div>
    </>
  );
}